import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IAuthState, ILoginCredentials, IAuthError, IRegisterData, IAuthResponse, IAuthUser } from '../types/auth.types';
import { TokenStorage } from '@/config/storage';
import { api, setNavigationCallback } from '@/services/api';
import { toast } from 'sonner';
import { AuthService } from '../services/auth.service';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { useTokenSync } from '@/hooks/useTokenSync';
import axios from 'axios';
import { API_URL } from '@/config/api';
import { hasInvalidTokens } from '@/utils/clearInvalidTokens';

interface IAuthContext {
  state: IAuthState;
  login: (credentials: ILoginCredentials) => Promise<IAuthResponse>;
  register: (data: IRegisterData) => Promise<IAuthResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: IAuthUser) => void;
  profile: IAuthUser | null;
}

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

// Valor padrão para o contexto
const defaultAuthContext: IAuthContext = {
  state: {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  },
  login: async () => {
    throw new Error('AuthProvider não inicializado');
  },
  register: async () => {
    throw new Error('AuthProvider não inicializado');
  },
  logout: async () => {
    throw new Error('AuthProvider não inicializado');
  },
  clearError: () => {
    throw new Error('AuthProvider não inicializado');
  },
  updateUser: () => {
    throw new Error('AuthProvider não inicializado');
  },
  profile: null,
};

interface AuthProviderProps {
  children: React.ReactNode;
}

function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<IAuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<IAuthError | null>(null);
  const [initialized, setInitialized] = useState(false);
  const sessionCheckInterval = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const location = useLocation();

  // Hook para renovação automática de token
  const { refreshToken, isTokenValid } = useTokenRefresh({
    checkInterval: 5 * 60 * 1000, // Verifica a cada 5 minutos (reduzido para ser mais responsivo)
    enableBackgroundRefresh: true,
    isAuthenticated: isAuthenticated, // Passa o estado de autenticação
    onTokenRefreshed: (newToken) => {
      console.log('[AuthProvider] Token renovado automaticamente');
      // Atualiza o token no axios
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    },
    onRefreshError: (error) => {
      console.error('[AuthProvider] Erro na renovação automática:', error);
      
      // Trata erros específicos de forma diferente
      if (error.message === 'Login em andamento') {
        console.log('[AuthProvider] Login em andamento, ignorando erro de refresh');
        return;
      }
      
      if (error.message === 'Refresh token muito frequente') {
        console.log('[AuthProvider] Refresh muito frequente, aguardando próxima tentativa');
        return;
      }
      
      // Em caso de erro, apenas registra o erro mas mantém a sessão
      // A sessão será mantida até que o usuário faça logout manualmente
      console.warn('[AuthProvider] Mantendo sessão ativa apesar do erro de renovação');
    },
  });

  // Hook para sincronização de tokens entre abas
  const { notifyTokenRefreshed, notifySessionCleared } = useTokenSync({
    enableSync: true,
    onTokenSynced: (accessToken, refreshToken) => {
      console.log('[AuthProvider] Token sincronizado de outra aba');
      // Atualiza o token no axios
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    },
    onSessionCleared: () => {
      console.log('[AuthProvider] Sessão limpa em outra aba - mantendo conexão local');
      // Não limpa a sessão local, apenas atualiza o estado se necessário
      // A sessão será mantida até que o usuário faça logout manualmente
    },
  });

  // Configura o callback de navegação para o serviço de API
  useEffect(() => {
    setNavigationCallback((path) => navigate(path, { replace: true }));
    return () => setNavigationCallback(() => {});
  }, [navigate]);

  const verifySession = useCallback(async () => {
    try {
      // Verifica se o token é válido localmente
      if (!isTokenValid()) {
        return false;
      }

      // Atualiza a última atividade
      TokenStorage.updateLastActivity();

      // Cria uma instância específica do axios para verificação sem interceptors
      const verifyApi = axios.create({
        baseURL: API_URL,
        timeout: 10000,
      });

      // Adiciona o token manualmente
      const token = TokenStorage.getAccessToken();
      if (token) {
        verifyApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      // Verifica no servidor sem usar o interceptor de renovação automática
      const response = await verifyApi.get('/api/auth/me');
      return response.status === 200;
    } catch {
      return false;
    }
  }, [isTokenValid]);

  // Effect para inicializar o estado de autenticação
  useEffect(() => {
    if (initialized) return;
    
    const initializeAuth = async () => {
      try {
        const storedUser = TokenStorage.getUser();
        const storedToken = TokenStorage.getAccessToken();
        
        // Verifica se há tokens inválidos e limpa se necessário
        if (hasInvalidTokens()) {
          console.warn('[AuthProvider] Tokens inválidos detectados, limpando...');
          TokenStorage.clearSession(false);
          setInitialized(true);
          setIsLoading(false);
          return;
        }
        
        // Se não há token ou usuário, não tenta verificar sessão
        if (!storedToken || !storedUser) {
          console.log('[AuthProvider] Nenhum token ou usuário encontrado, pulando verificação de sessão');
          setInitialized(true);
          setIsLoading(false);
          return;
        }
        
        const isSessionValid = await verifySession();

        if (storedUser && isSessionValid) {
          setUser(storedUser);
          setIsAuthenticated(true);
          
          // Configura o token no axios
          const token = TokenStorage.getAccessToken();
          if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
        } else {
          console.warn('[AuthProvider] Sessão inválida na inicialização - limpando tokens');
          // Limpa tokens inválidos para evitar problemas futuros
          TokenStorage.clearSession(false);
        }
      } catch (error) {
        console.error('Erro ao inicializar autenticação:', error);
        console.warn('[AuthProvider] Erro na inicialização - limpando tokens');
        // Limpa tokens em caso de erro para evitar problemas futuros
        TokenStorage.clearSession(false);
      } finally {
        setInitialized(true);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [initialized, verifySession, navigate, location.pathname]);

  // Verificação periódica da sessão
  useEffect(() => {
    if (!isAuthenticated) {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
      return;
    }

                const checkSession = async () => {
        const isValid = await verifySession();
        if (!isValid) {
          console.warn('[AuthProvider] Sessão inválida detectada - mantendo conexão ativa');
          // Não limpa a sessão automaticamente, apenas registra o aviso
          // A sessão será mantida até que o usuário faça logout manualmente
        }
      };

    // Verifica a cada 5 minutos
    sessionCheckInterval.current = setInterval(checkSession, 5 * 60 * 1000);

    // Limpa o intervalo quando o componente é desmontado
    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
    };
  }, [isAuthenticated, verifySession, navigate]);

  // Atualiza a última atividade quando há interação do usuário
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      TokenStorage.updateLastActivity();
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [isAuthenticated]);

  const login = async (credentials: ILoginCredentials): Promise<IAuthResponse> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await AuthService.login(credentials);

      if (response.access_token && response.user) {
        TokenStorage.setTokens(response.access_token, response.refresh_token || '');
        TokenStorage.setUser(response.user);
        
        setUser(response.user);
        setIsAuthenticated(true);

        api.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;
        
        // Notifica outras abas sobre o login
        notifyTokenRefreshed(response.access_token, response.refresh_token || '');
      } else {
        if (response.message) {
          const authError = {
            message: response.message,
            code: response.status || 'AUTH_ERROR'
          };
          setError(authError);
        }
      }

      return response;
    } catch (error: any) {
      const authError = {
        message: error.message || 'Erro ao fazer login',
        code: error.code || 'AUTH_ERROR'
      };
      setError(authError);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: IRegisterData): Promise<IAuthResponse> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await AuthService.register(data);

      if (response.access_token && response.user) {
        TokenStorage.setTokens(response.access_token, response.refresh_token || '');
        TokenStorage.setUser(response.user);
        
        setUser(response.user);
        setIsAuthenticated(true);

        api.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;
      } else {
        if (response.message) {
          const authError = {
            message: response.message,
            code: response.status || 'AUTH_ERROR'
          };
          setError(authError);
        }
      }

      return response;
    } catch (error: any) {
      const authError = {
        message: error.message || 'Erro ao registrar',
        code: error.code || 'AUTH_ERROR'
      };
      setError(authError);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (isAuthenticated) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Erro ao fazer logout no servidor:', error);
    } finally {
      // Limpa a sessão manualmente (apenas quando o usuário faz logout)
      TokenStorage.clearSession(true); // Notifica outras abas sobre o logout manual
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      
      // Notifica outras abas sobre o logout
      notifySessionCleared();
      
      navigate('/autenticacao', { replace: true });
    }
  };

  const clearError = () => {
    setError(null);
  };

  const updateUser = (updatedUser: IAuthUser) => {
    TokenStorage.setUser(updatedUser);
    setUser(updatedUser);
  };

  const state: IAuthState = {
    user,
    isAuthenticated,
    isLoading,
    error,
  };

  // Garante que o contexto seja sempre válido, mesmo durante a inicialização
  const contextValue: IAuthContext = {
    state,
    login,
    register,
    logout,
    clearError,
    updateUser,
    profile: user,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export default AuthProvider; 